import { useState } from 'react';
import { useDispatch } from 'react-redux';
import { TYPE } from 'store/browse-items/fixtures';
import * as yup from 'yup';
import BatchConfirm from 'library/components/modal/batch-confirm';
import useSetList from 'library/hooks/use-deferred-data/use-set-list';
import useProjectList from 'library/hooks/use-deferred-data/use-project-list';
import AddToContainerEntityModal from '../modal/pick-entity';
import { batchAddToProject } from '../../../store/project/actions';
import { batchAddToSet } from '../../../store/set/actions';

const ENTITY_MODAL_CONFIG = {
  [TYPE.SET]: {
    isSetList: true,
    isMultipleAllowed: false,
    title: 'Add item to set',
    placeholder: 'Search for sets',
  },
  [TYPE.PROJECT]: {
    isProjectList: true,
    isMultipleAllowed: false,
    title: 'Add item to project',
    placeholder: 'Search for projects',
  },
};

const ENTITY_SUBMIT_CONFIG = {
  [TYPE.SET]: {
    messages: { success: 'Item added to set' },
  },
  [TYPE.PROJECT]: {
    type: 'ITEM',
    messages: { success: 'Item added to project' },
  },
};

const entityValidation = yup.object().shape({
  id: yup.number().required(),
});

const STEPS = {
  PENDING: 0,
  CONFIRM_ITEMS: 1,
  PICK_ENTITY: 2,
};

const BatchAddWizard = ({
  type, data, onDeselect, onClose, entityType, config,
}) => {
  const { options: setOptions, isLoading: isSetLoading, onQueryChange: onSetQueryChange } = useSetList(entityType === TYPE.SET);
  const { options: projectOptions, isLoading: isProjectLoading, onQueryChange: onProjectQueryChange } = useProjectList(entityType === TYPE.PROJECT);
  const ENTITY_DATA_CONFIG = {
    [TYPE.SET]: {
      entityOptions: setOptions,
      isEntityListLoading: isSetLoading,
      onSubmit: batchAddToSet,
      onSearch: onSetQueryChange,
    },
    [TYPE.PROJECT]: {
      entityOptions: projectOptions,
      isEntityListLoading: isProjectLoading,
      onSubmit: batchAddToProject,
      onSearch: onProjectQueryChange,
    },
  };
  const {
    isEntityListLoading, entityOptions, onSubmit, onSearch,
  } = ENTITY_DATA_CONFIG[type];
  const dispatch = useDispatch();
  const [step, setStep] = useState(STEPS.CONFIRM_ITEMS);

  const handleItemIdsConfirm = () => {
    setStep(STEPS.PICK_ENTITY);
  };

  const handleQuit = () => {
    onClose();
  };

  const onEntityModalSubmit = ({ id }) => {
    dispatch(onSubmit({
      ...ENTITY_SUBMIT_CONFIG[type],
      ...config,
      ...(entityType === TYPE.SET ? { type: 'SET' } : {}),
      ids: data.map(({ id: entityId }) => entityId),
      id,
    }));

    onClose();
  };

  return (
    <>
      <BatchConfirm
        title={`Add ${data.length} items to ${type}`}
        text={`You are about to add the following items to the ${type}`}
        isOpened={step === STEPS.CONFIRM_ITEMS}
        onClose={handleQuit}
        onDeselect={onDeselect}
        onSubmit={handleItemIdsConfirm}
        data={data}
        submitText={`Yes, add ${data.length} items to ${type}`}
      />
      <AddToContainerEntityModal
        {...ENTITY_MODAL_CONFIG[type]}
        name="id"
        submitText="Save changes"
        cancelText="Cancel"
        overlayClassName="Overlay"
        cancelWidth="94px"
        defaultValue={undefined}
        isOpened={step === STEPS.PICK_ENTITY}
        isLoading={isEntityListLoading}
        onClose={handleQuit}
        onSubmit={onEntityModalSubmit}
        options={entityOptions}
        validation={entityValidation}
        onSearch={onSearch}
      />
    </>
  );
};

export default BatchAddWizard;
